/**
 * OpportunityDetail — UEIM-01 + UEIM-02 + UEIM-03
 * Canonical workspace for an Opportunity. Tabs: Overview | Design | Scope | Requirements | Commercial
 * The Design tab hosts the Moodboard Designer.
 * Mounted at /crm/opportunities/:oppId
 */
import { useState, useEffect, useCallback } from "react";import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, FileText, Palette, ListChecks, ClipboardList, DollarSign, CalendarDays,
  Building2, User, Calendar, Tag, AlertCircle, Loader2,
  Package, ExternalLink, Plus, CreditCard, BookOpen, ChevronRight, RefreshCw,
} from "lucide-react";
import { moodboardApi, sourcingApi, bookingApi, requirementsApi } from "@/services/api";
import api from "@/services/api";
import { useAuth } from "@/contexts/AuthContext";
import { hasCapability } from "@/services/api";
import EventRequirementsPanel from "@/components/EventRequirementsPanel";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import MoodboardDesigner from "./MoodboardDesigner";
import CommercialTab from "./CommercialTab";
import EventCoreTab from "./EventCoreTab";
import { InterventionBanner } from "@/components/InterventionBanner";
import { EventStageBar } from "@/components/EventStageBar";
import { WorkflowActionBar } from "@/components/WorkflowActionBar";

// ── Palette ────────────────────────────────────────────────────────────────────
const C = {
  ivory:    "#FBF7F2",
  sand:     "#FAF6F0",
  gold:     "#C9A15F",
  charcoal: "#3A3330",
  slate:    "#7A706A",
  divider:  "rgba(201,161,95,0.15)",
};

// ── Opportunity stage order (canonical production pipeline) ───────────────────
// Source: backend/uat_data_generator.py OPP_STAGES + start_planning_service.py
// Primary production path: INQUIRY → RFP → PROPOSAL → TECHNICAL_REVIEW → NEGOTIATION → CONTRACT → CLOSED_WON
// CRM-seed stages (PROSPECTING, QUALIFICATION) are prepended to handle legacy records.
// DECISION_MAKERS, VALUE_PROPOSITION, etc. are NOT in the canonical order; no auto-advance for those.
const OPP_STAGE_ORDER = [
  "PROSPECTING", "QUALIFICATION",
  "INQUIRY", "RFP", "PROPOSAL", "TECHNICAL_REVIEW",
  "NEGOTIATION", "CONTRACT", "CLOSED_WON",
];
const OPP_STAGES = [
  { id: "PROSPECTING",     label: "Prospecting" },
  { id: "QUALIFICATION",   label: "Qualification" },
  { id: "INQUIRY",         label: "Inquiry" },
  { id: "RFP",             label: "RFP" },
  { id: "PROPOSAL",        label: "Proposal" },
  { id: "TECHNICAL_REVIEW",label: "Tech Review" },
  { id: "NEGOTIATION",     label: "Negotiation" },
  { id: "CONTRACT",        label: "Contract" },
  { id: "CLOSED_WON",      label: "Won" },
];
const OPP_TERMINAL = { id: "CLOSED_LOST", label: "Lost" };
// Stages that require estimated_value before advancing into them
const PROPOSAL_STAGES = new Set(["PROPOSAL", "TECHNICAL_REVIEW", "NEGOTIATION", "CONTRACT", "CLOSED_WON"]);

const STAGE_LABELS = {
  PROSPECTING: "Prospecting", QUALIFICATION: "Qualification",
  NEEDS_ANALYSIS: "Needs Analysis", VALUE_PROPOSITION: "Value Proposition",
  DECISION_MAKERS: "Decision Makers", PERCEPTION_ANALYSIS: "Perception Analysis",
  INQUIRY: "Inquiry", RFP: "RFP", PROPOSAL: "Proposal",
  TECHNICAL_REVIEW: "Technical Review", NEGOTIATION: "Negotiation",
  CONTRACT: "Contract",
  CLOSED_WON: "Closed Won", CLOSED_LOST: "Closed Lost",
};

const STAGE_COLORS = {
  CLOSED_WON:  { bg: "#E8F5EC", text: "#2E7D4F", border: "rgba(46,125,79,0.25)" },
  CLOSED_LOST: { bg: "#FEE2E2", text: "#C64040", border: "rgba(198,64,64,0.25)" },
};
const DEFAULT_STAGE_COLOR = { bg: "#EEF4FF", text: "#0176D3", border: "rgba(1,118,211,0.20)" };

const TABS = [
  { id: "overview",     label: "Overview",     icon: FileText },
  { id: "design",       label: "Event Design", icon: Palette },
  { id: "scope",        label: "Scope",        icon: ListChecks },
  { id: "requirements", label: "Requirements", icon: ClipboardList },
  { id: "event_brief",  label: "Event Brief",  icon: BookOpen },
  { id: "commercial",   label: "Commercial",   icon: DollarSign },
  { id: "booking",      label: "Booking",      icon: CreditCard },
  { id: "eventcore",    label: "Event Core",   icon: CalendarDays },
];

// ── Overview tab ───────────────────────────────────────────────────────────────
function OverviewTab({ opp }) {
  const fields = [
    { label: "Stage",          value: STAGE_LABELS[opp.stage] || opp.stage },
    { label: "Account",        value: opp.account_name || "—" },
    { label: "Contact",        value: opp.contact_name || "—" },
    { label: "Owner",          value: opp.owner_name || "—" },
    { label: "Expected Close", value: opp.expected_close_date ? new Date(opp.expected_close_date).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—" },
    { label: "Value",          value: opp.value ? `₹ ${Number(opp.value).toLocaleString("en-IN")}` : "—" },
    { label: "Event Date",     value: opp.event_date || "—" },
    { label: "Guest Count",    value: opp.guest_count || "—" },
    { label: "Probability",    value: opp.probability ? `${opp.probability}%` : "—" },
  ];

  return (
    <div className="p-6">
      {opp.description && (
        <p className="text-sm mb-6" style={{ color: C.slate }}>{opp.description}</p>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {fields.map(f => (
          <div
            key={f.label}
            className="rounded-lg p-3.5"
            style={{ background: C.sand, border: `1px solid ${C.divider}` }}
          >
            <p className="text-[10px] font-semibold uppercase tracking-wide mb-1" style={{ color: C.slate }}>
              {f.label}
            </p>
            <p className="text-sm font-medium" style={{ color: C.charcoal }}>{f.value}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Scope tab ─────────────────────────────────────────────────────────────────
function ScopeTab({ oppId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    moodboardApi.listScopeItems({ opportunity_id: oppId })
      .then(r => setItems(r.data || []))
      .finally(() => setLoading(false));
  }, [oppId]);

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={18} className="animate-spin" style={{ color: C.slate }} />
    </div>
  );

  if (!items.length) return (
    <div className="p-8 text-center">
      <ListChecks size={32} className="mx-auto mb-3 opacity-20" style={{ color: C.slate }} />
      <p className="text-sm" style={{ color: C.slate }}>No scope items yet.</p>
      <p className="text-xs mt-1" style={{ color: C.slate }}>Approve design elements in the Event Design tab, then convert them to scope items.</p>
    </div>
  );

  const PRIORITY_COLORS = {
    MUST_HAVE:    { bg: "#FEE2E2", text: "#C64040", border: "rgba(198,64,64,0.25)" },
    SHOULD_HAVE:  { bg: "#FEF3C7", text: "#92400E", border: "rgba(146,64,14,0.20)" },
    NICE_TO_HAVE: { bg: "#F4F4F4", text: "#706E6B", border: "rgba(112,110,107,0.20)" },
  };

  return (
    <div className="p-6 space-y-3">
      <p className="text-sm font-medium mb-4" style={{ color: C.slate }}>{items.length} scope item{items.length !== 1 ? "s" : ""}</p>
      {items.map(item => {
        const pc = PRIORITY_COLORS[item.priority] || PRIORITY_COLORS.NICE_TO_HAVE;
        return (
          <div
            key={item.id}
            className="rounded-xl p-4 flex items-start gap-3"
            style={{ background: C.sand, border: `1px solid ${C.divider}` }}
            data-testid={`scope-item-${item.id}`}
          >
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-1">
                <h4 className="text-sm font-semibold" style={{ color: C.charcoal }}>{item.title}</h4>
                <span className="text-[10px] px-1.5 py-0.5 rounded font-medium"
                  style={{ background: pc.bg, color: pc.text, border: `1px solid ${pc.border}` }}>
                  {item.priority?.replace(/_/g, " ")}
                </span>
              </div>
              {item.description && (
                <p className="text-xs" style={{ color: C.slate }}>{item.description}</p>
              )}
              <div className="flex gap-2 mt-1.5 text-xs" style={{ color: C.slate }}>
                {item.quantity && <span>{item.quantity} {item.unit || ""}</span>}
                {item.estimated_budget_range && <span>· {item.estimated_budget_range}</span>}
              </div>
            </div>
            <span
              className="text-[10px] px-1.5 py-0.5 rounded font-medium shrink-0"
              style={{ background: "#EEF4FF", color: "#0176D3", border: "1px solid rgba(1,118,211,0.2)" }}
            >
              {item.status}
            </span>
          </div>
        );
      })}
    </div>
  );
}

// ── Requirements tab ──────────────────────────────────────────────────────────
function RequirementsTab({ oppId }) {
  const [scopeItems, setScopeItems] = useState([]);
  const [requirements, setRequirements] = useState({});
  const [loading, setLoading] = useState(true);
  const [newReq, setNewReq] = useState({});
  const [adding, setAdding] = useState(null);
  const [categories, setCategories] = useState([]);
  const [pkgModal, setPkgModal] = useState(null);  // { scopeItem, reqItems }
  const [pkgForm, setPkgForm] = useState({ title: "", description: "", service_category_id: "", response_deadline: "" });
  const [creatingPkg, setCreatingPkg] = useState(false);
  const [existingPkgs, setExistingPkgs] = useState([]);  // packages for this opportunity
  const { user } = useAuth();
  const navigate = useNavigate();
  const canManage      = hasCapability(user, "moodboard.manage");
  const canCreatePkg   = hasCapability(user, "sourcing.package.create");
  const canReadSourcing = hasCapability(user, "sourcing.read");

  const loadData = useCallback(async () => {
    try {
      const [scopeRes, catRes] = await Promise.all([
        moodboardApi.listScopeItems({ opportunity_id: oppId }),
        moodboardApi.listServiceCategories(),
      ]);
      const items = scopeRes.data || [];
      setScopeItems(items);
      setCategories(catRes.data || []);
      const reqs = {};
      await Promise.all(items.map(async i => {
        const r2 = await moodboardApi.listRequirements(i.id);
        reqs[i.id] = r2.data || [];
      }));
      setRequirements(reqs);
      // Load existing sourcing packages
      if (canReadSourcing) {
        const pkgRes = await sourcingApi.listPackages({ opportunity_id: oppId });
        setExistingPkgs(pkgRes.data?.results || []);
      }
    } catch (e) {
      // silently log — requirements tab may be partially accessible
    } finally {
      setLoading(false);
    }
  }, [oppId, canReadSourcing]);

  useEffect(() => { loadData(); }, [loadData]);

  const handleAddRequirement = async (sid) => {
    const form = newReq[sid] || {};
    if (!form.description) return;
    setAdding(sid);
    try {
      const r = await moodboardApi.createRequirement(sid, {
        requirement_type: form.type || "SUPPLY",
        description: form.description,
        quantity: form.quantity ? Number(form.quantity) : undefined,
        unit: form.unit,
        requested_delivery_start: form.delivery_start || undefined,
        requested_delivery_end: form.delivery_end || undefined,
        delivery_location: form.delivery_location || undefined,
        delivery_notes: form.delivery_notes || undefined,
      });
      setRequirements(prev => ({ ...prev, [sid]: [...(prev[sid] || []), r.data] }));
      setNewReq(prev => ({ ...prev, [sid]: {} }));
      toast.success("Requirement added");
    } finally {
      setAdding(null);
    }
  };

  const openPkgModal = (si, reqs) => {
    const catName = categories[0]?.name || "";
    setPkgForm({
      title: `${si.title} — Sourcing`,
      description: "",
      service_category_id: categories[0]?._id || categories[0]?.id || "",
      response_deadline: "",
    });
    setPkgModal({ scopeItem: si, reqItems: reqs.filter(r => r.status !== "SOURCING_SELECTED") });
  };

  const handleCreatePackage = async () => {
    if (!pkgForm.title || !pkgForm.service_category_id) {
      toast.error("Title and service category are required");
      return;
    }
    const reqIds = pkgModal.reqItems.map(r => r.id);
    if (!reqIds.length) {
      toast.error("No eligible requirement items to package");
      return;
    }
    setCreatingPkg(true);
    try {
      const res = await sourcingApi.createPackage({
        opportunity_id: oppId,
        title: pkgForm.title,
        description: pkgForm.description || undefined,
        service_category_id: pkgForm.service_category_id,
        requirement_item_ids: reqIds,
        response_deadline: pkgForm.response_deadline || undefined,
      });
      toast.success("Sourcing package created");
      setPkgModal(null);
      navigate(`/crm/sourcing-packages/${res.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to create package");
    } finally {
      setCreatingPkg(false);
    }
  };

  const getPkgForReq = (reqId) => {
    return existingPkgs.find(p => {
      // We check if any package has this req item — simplistic: look up by req status
      return false;  // proper check requires package_items endpoint; links handled via navigation
    });
  };

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={18} className="animate-spin" style={{ color: C.slate }} />
    </div>
  );

  if (!scopeItems.length) return (
    <div className="p-8 text-center">
      <ClipboardList size={32} className="mx-auto mb-3 opacity-20" style={{ color: C.slate }} />
      <p className="text-sm" style={{ color: C.slate }}>No scope items to generate requirements from.</p>
    </div>
  );

  return (
    <div className="p-6 space-y-6">
      {/* Sourcing packages quick-access */}
      {canReadSourcing && existingPkgs.length > 0 && (
        <div className="rounded-xl overflow-hidden border" style={{ borderColor: C.divider }}>
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: "#EDF2FF", borderBottom: `1px solid ${C.divider}` }}>
            <div className="flex items-center gap-2">
              <Package size={14} className="text-indigo-600" />
              <span className="text-sm font-semibold text-indigo-800">Sourcing Packages</span>
            </div>
          </div>
          <div className="divide-y" style={{ background: C.ivory, divideColor: C.divider }}>
            {existingPkgs.map(pkg => (
              <div key={pkg.id} className="px-4 py-2.5 flex items-center justify-between" data-testid={`pkg-link-${pkg.id}`}>
                <div>
                  <p className="text-sm font-medium" style={{ color: C.charcoal }}>{pkg.title}</p>
                  <p className="text-xs" style={{ color: C.slate }}>{pkg.service_category_name} · {pkg.status}</p>
                </div>
                <Link to={`/crm/sourcing-packages/${pkg.id}`} className="flex items-center gap-1 text-xs text-indigo-600 hover:underline">
                  Open <ExternalLink size={11} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {scopeItems.map(si => (
        <div key={si.id}
          className="rounded-xl overflow-hidden"
          style={{ border: `1px solid ${C.divider}` }}
        >
          <div className="px-4 py-3 flex items-center justify-between" style={{ background: C.sand, borderBottom: `1px solid ${C.divider}` }}>
            <h4 className="text-sm font-semibold" style={{ color: C.charcoal }}>{si.title}</h4>
            {canCreatePkg && (requirements[si.id] || []).some(r => r.status !== "SOURCING_SELECTED") && (
              <button
                onClick={() => openPkgModal(si, requirements[si.id] || [])}
                className="flex items-center gap-1 text-xs px-2.5 py-1 rounded-md font-medium text-indigo-700 bg-indigo-50 hover:bg-indigo-100 border border-indigo-200 transition-colors"
                data-testid={`create-package-btn-${si.id}`}
              >
                <Package size={11} /> Create Sourcing Package
              </button>
            )}
          </div>
          <div style={{ background: C.ivory }}>
            {(requirements[si.id] || []).map(req => (
              <div key={req.id} className="px-4 py-3 border-b last:border-b-0" style={{ borderColor: C.divider }}>
                <div className="flex items-start gap-2">
                  <span
                    className="text-[10px] px-1.5 py-0.5 rounded font-mono shrink-0 mt-0.5"
                    style={{ background: "#F4F4F4", color: "#706E6B", border: "1px solid rgba(112,110,107,0.2)" }}
                  >
                    {req.requirement_type}
                  </span>
                  <div className="flex-1">
                    <p className="text-sm" style={{ color: C.charcoal }}>{req.description}</p>
                    {(req.quantity || req.unit) && (
                      <p className="text-xs mt-0.5" style={{ color: C.slate }}>
                        {req.quantity} {req.unit}
                        {req.delivery_location ? ` · ${req.delivery_location}` : ""}
                        {req.requested_delivery_start ? ` · From ${req.requested_delivery_start}` : ""}
                      </p>
                    )}
                    {req.status === "SOURCING_SELECTED" && (
                      <span className="inline-flex items-center gap-1 text-[10px] mt-1 px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700 font-medium">
                        <Package size={9} /> Sourcing Selected
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
            {canManage && (
              <div className="px-4 py-3 space-y-2">
                <div className="flex gap-2 flex-wrap">
                  <select
                    className="text-xs border rounded px-2 py-1.5"
                    style={{ borderColor: C.divider }}
                    value={newReq[si.id]?.type || "SUPPLY"}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], type: e.target.value } }))}
                  >
                    {["SUPPLY","INSTALL","MANAGE","PERMIT"].map(t => <option key={t}>{t}</option>)}
                  </select>
                  <input
                    className="flex-1 text-xs border rounded px-2 py-1.5 min-w-48"
                    style={{ borderColor: C.divider }}
                    placeholder="Requirement description…"
                    value={newReq[si.id]?.description || ""}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], description: e.target.value } }))}
                    onKeyDown={e => e.key === "Enter" && handleAddRequirement(si.id)}
                    data-testid={`req-description-${si.id}`}
                  />
                  <input
                    className="w-16 text-xs border rounded px-2 py-1.5"
                    style={{ borderColor: C.divider }}
                    placeholder="Qty"
                    type="number"
                    value={newReq[si.id]?.quantity || ""}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], quantity: e.target.value } }))}
                  />
                  <input
                    className="w-20 text-xs border rounded px-2 py-1.5"
                    style={{ borderColor: C.divider }}
                    placeholder="Unit"
                    value={newReq[si.id]?.unit || ""}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], unit: e.target.value } }))}
                  />
                </div>
                {/* Delivery context fields */}
                <div className="flex gap-2 flex-wrap">
                  <input
                    className="text-xs border rounded px-2 py-1.5 w-36"
                    style={{ borderColor: C.divider }}
                    type="date"
                    title="Delivery Start"
                    placeholder="Delivery Start"
                    value={newReq[si.id]?.delivery_start || ""}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], delivery_start: e.target.value } }))}
                  />
                  <input
                    className="text-xs border rounded px-2 py-1.5 w-36"
                    style={{ borderColor: C.divider }}
                    type="date"
                    title="Delivery End"
                    value={newReq[si.id]?.delivery_end || ""}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], delivery_end: e.target.value } }))}
                  />
                  <input
                    className="flex-1 text-xs border rounded px-2 py-1.5 min-w-32"
                    style={{ borderColor: C.divider }}
                    placeholder="Delivery location"
                    value={newReq[si.id]?.delivery_location || ""}
                    onChange={e => setNewReq(p => ({ ...p, [si.id]: { ...p[si.id], delivery_location: e.target.value } }))}
                  />
                  <button
                    onClick={() => handleAddRequirement(si.id)}
                    disabled={adding === si.id || !newReq[si.id]?.description}
                    className="text-xs px-3 py-1.5 rounded font-medium transition-opacity"
                    style={{ background: C.gold, color: "#fff", opacity: adding === si.id ? 0.5 : 1 }}
                    data-testid={`add-req-btn-${si.id}`}
                  >
                    {adding === si.id ? "Adding…" : "+ Add"}
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}

      {/* Create Sourcing Package Modal */}
      <Dialog open={!!pkgModal} onOpenChange={() => setPkgModal(null)}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Package size={18} className="text-indigo-600" /> Create Sourcing Package
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-slate-600">
              This will create a sourcing package for <strong>{pkgModal?.reqItems?.length || 0}</strong> requirement item(s) from scope "<strong>{pkgModal?.scopeItem?.title}</strong>".
            </p>
            <div>
              <Label>Package Title *</Label>
              <Input value={pkgForm.title} onChange={e => setPkgForm(f => ({...f, title: e.target.value}))} placeholder="Decor — Stage & Entry Sourcing" className="mt-1" data-testid="pkg-form-title" />
            </div>
            <div>
              <Label>Service Category *</Label>
              <select
                className="w-full mt-1 border border-slate-300 rounded-md px-3 py-2 text-sm"
                value={pkgForm.service_category_id}
                onChange={e => setPkgForm(f => ({...f, service_category_id: e.target.value}))}
                data-testid="pkg-form-category"
              >
                <option value="">— Select a category —</option>
                {categories.map(c => (
                  <option key={c.id || c._id} value={c.id || c._id}>{c.name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label>Response Deadline</Label>
              <Input type="date" value={pkgForm.response_deadline} onChange={e => setPkgForm(f => ({...f, response_deadline: e.target.value}))} className="mt-1" data-testid="pkg-form-deadline" />
            </div>
            <div>
              <Label>Description (optional)</Label>
              <Input value={pkgForm.description} onChange={e => setPkgForm(f => ({...f, description: e.target.value}))} placeholder="Optional notes for this package" className="mt-1" />
            </div>
            <div className="flex justify-end gap-3 pt-1 border-t">
              <Button variant="outline" onClick={() => setPkgModal(null)}>Cancel</Button>
              <Button onClick={handleCreatePackage} disabled={creatingPkg} data-testid="confirm-create-pkg-btn">
                {creatingPkg ? "Creating…" : "Create & Open Package"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ── BookingTab — embedded booking view within OpportunityDetail ───────────────
function BookingTab({ oppId, opp }) {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const canRead    = hasCapability(user, "booking.read");
  const canCreate  = hasCapability(user, "booking.create");

  const STATUS_CONFIG = {
    DRAFT:              { bg: "#F3F4F6", text: "#374151", label: "Draft" },
    PENDING_ACCEPTANCE: { bg: "#FEF3C7", text: "#92400E", label: "Pending Acceptance" },
    CONFIRMED:          { bg: "#D1FAE5", text: "#065F46", label: "Confirmed" },
    COMPLETED:          { bg: "#DBEAFE", text: "#1E40AF", label: "Completed" },
    CANCELLED:          { bg: "#FEE2E2", text: "#991B1B", label: "Cancelled" },
  };

  const fmtCurrency = (v, currency = "INR") =>
    v != null ? `${currency === "INR" ? "₹" : currency} ${Number(v).toLocaleString("en-IN")}` : "—";

  const fmtDate = (d) => d ? new Date(d).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—";

  const fetchBookings = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    try {
      const { data } = await bookingApi.list({ opportunity_id: oppId });
      setBookings(data.items || []);
    } catch {
      toast.error("Failed to load bookings");
    } finally {
      setLoading(false);
    }
  }, [oppId, canRead]);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  if (!canRead) return (
    <div className="p-6 text-sm text-center" style={{ color: C.slate }}>
      You don't have permission to view bookings.
    </div>
  );

  if (loading) return (
    <div className="flex items-center justify-center py-12">
      <Loader2 size={18} className="animate-spin" style={{ color: C.slate }} />
    </div>
  );

  return (
    <div data-testid="opp-booking-tab" className="p-6 space-y-4">
      <div className="flex items-center justify-between">
        <div className="text-sm font-medium" style={{ color: C.charcoal }}>
          Bookings for this Opportunity
        </div>
        <div className="flex gap-2">
          <button onClick={fetchBookings} className="p-1 rounded hover:bg-black/5">
            <RefreshCw className="w-3.5 h-3.5" style={{ color: C.slate }} />
          </button>
        </div>
      </div>

      {bookings.length === 0 ? (
        <div className="rounded-xl p-8 text-center" style={{ background: C.sand, border: `1px solid ${C.divider}` }}>
          <CreditCard size={24} className="mx-auto mb-3 opacity-30" style={{ color: C.slate }} />
          <p className="text-sm" style={{ color: C.slate }}>No bookings yet for this opportunity</p>
          {canCreate && (
            <p className="text-xs mt-2" style={{ color: C.slate }}>
              Bookings are created from accepted proposal revisions.
              Go to <button className="underline" style={{ color: C.gold }} onClick={() => {}}>Commercial tab</button> to accept a proposal first.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-2">
          {bookings.map(b => {
            const cfg = STATUS_CONFIG[b.status] || STATUS_CONFIG.DRAFT;
            return (
              <div
                key={b.id}
                data-testid={`opp-booking-${b.id}`}
                className="rounded-xl p-4 flex items-center gap-4 cursor-pointer hover:shadow-sm transition-all"
                style={{ background: C.sand, border: `1px solid ${C.divider}` }}
                onClick={() => navigate(`/crm/bookings/${b.id}`)}
              >
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-sm" style={{ color: C.charcoal }}>{b.booking_number}</span>
                    <span className="px-2 py-0.5 text-xs font-medium rounded-full" style={{ background: cfg.bg, color: cfg.text }}>
                      {cfg.label}
                    </span>
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: C.slate }}>
                    Created {fmtDate(b.created_at)}
                    {b.confirmed_at && ` · Confirmed ${fmtDate(b.confirmed_at)}`}
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-sm font-semibold" style={{ color: C.charcoal }}>
                    {fmtCurrency(b.booking_value, b.currency)}
                  </div>
                </div>
                <ChevronRight className="w-4 h-4 shrink-0" style={{ color: C.slate }} />
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────
export default function OpportunityDetail() {
  const { oppId } = useParams();
  const navigate   = useNavigate();
  const { user }   = useAuth();
  const [opp,       setOpp]       = useState(null);
  const [loading,   setLoading]   = useState(true);
  const [error,     setError]     = useState(null);
  const [tab,       setTab]       = useState("design");
  const [advancing, setAdvancing] = useState(false);

  const canUpdate = hasCapability(user, "crm.opportunity.update");

  useEffect(() => {
    api.get(`/crm/opportunities/${oppId}`)
      .then(r => setOpp(r.data))
      .catch(() => {
        return api.get("/crm/opportunities", { params: { limit: 1, id: oppId } })
          .then(r => {
            const items = r.data?.results || r.data || [];
            const found = items.find(o => o.id === oppId || o._id === oppId);
            setOpp(found || null);
          })
          .catch(() => setError("Opportunity not found."));
      })
      .finally(() => setLoading(false));
  }, [oppId]);

  if (loading) return (
    <div className="flex items-center justify-center min-h-screen" style={{ background: C.ivory }}>
      <Loader2 size={24} className="animate-spin" style={{ color: C.gold }} />
    </div>
  );

  if (error || !opp) return (
    <div className="flex flex-col items-center justify-center min-h-screen gap-3" style={{ background: C.ivory }}>
      <AlertCircle size={32} style={{ color: "#C64040" }} />
      <p className="text-sm" style={{ color: C.slate }}>{error || "Opportunity not found."}</p>
      <button onClick={() => navigate("/crm/opportunities")} className="text-xs underline" style={{ color: C.gold }}>
        Back to Opportunities
      </button>
    </div>
  );

  const stageColor = STAGE_COLORS[opp.stage] || DEFAULT_STAGE_COLOR;

  // ── Stage advance logic ──────────────────────────────────────────────────────
  const currentStageIdx = OPP_STAGE_ORDER.indexOf(opp.stage);
  const nextStage       = currentStageIdx >= 0 && currentStageIdx < OPP_STAGE_ORDER.length - 1
    ? OPP_STAGE_ORDER[currentStageIdx + 1]
    : null;
  const nextStageLabel  = nextStage ? (STAGE_LABELS[nextStage] || nextStage) : null;

  const handleAdvanceStage = async () => {
    if (!nextStage) return;
    // Inline validation: PROPOSAL+ requires estimated_value
    if (PROPOSAL_STAGES.has(nextStage) && !opp.estimated_value) {
      toast.error(`Estimated value is required before advancing to ${nextStageLabel}. Update the opportunity value first.`);
      return;
    }
    setAdvancing(true);
    try {
      const { data } = await api.patch(`/crm/opportunities/${oppId}`, { stage: nextStage });
      toast.success(`Advanced to ${nextStageLabel}`);
      setOpp(data);
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.response?.data?.detail || "Failed to advance stage");
    } finally {
      setAdvancing(false);
    }
  };

  const handleStartPlanning = async () => {
    setAdvancing(true);
    try {
      await api.post(`/crm/opportunities/${oppId}/start-planning`);
      toast.success("Event planning started");
      navigate("/crm/events");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to start event planning");
    } finally {
      setAdvancing(false);
    }
  };

  // Determine primary action for WorkflowActionBar
  const primaryAction = (() => {
    if (!canUpdate) return null;
    if (opp.stage === "CLOSED_WON") {
      return {
        label: advancing ? "Starting…" : "Start Event Planning",
        loading: advancing,
        style: { background: C.gold, color: "#fff" },
        onClick: handleStartPlanning,
      };
    }
    if (opp.stage === "CLOSED_LOST") return null;
    if (!nextStage) return null;
    const blockedByValue = PROPOSAL_STAGES.has(nextStage) && !opp.estimated_value;
    return {
      label: advancing ? "Advancing…" : `Advance to ${nextStageLabel}`,
      loading: advancing,
      disabled: blockedByValue,
      style: { background: C.gold, color: "#fff" },
      onClick: handleAdvanceStage,
    };
  })();

  const advanceHint = (() => {
    if (!primaryAction) return undefined;
    if (opp.stage === "CLOSED_WON") return "Opportunity won. Create the event record to begin planning.";
    if (nextStage && PROPOSAL_STAGES.has(nextStage) && !opp.estimated_value)
      return "Set an estimated value on this opportunity before advancing to Proposal stage.";
    return `Current stage: ${STAGE_LABELS[opp.stage] || opp.stage}. Next: ${nextStageLabel}.`;
  })();

  return (
    <div className="min-h-screen" style={{ background: C.ivory }}>
      {/* Header */}
      <div
        className="sticky top-0 z-20 px-4 sm:px-6 py-3"
        style={{ background: C.ivory, borderBottom: `1px solid ${C.divider}` }}
      >
        <div className="max-w-6xl mx-auto">
          <button
            onClick={() => navigate("/crm/opportunities")}
            className="flex items-center gap-1.5 text-xs mb-2 hover:opacity-70"
            style={{ color: C.slate }}
          >
            <ArrowLeft size={12} /> Opportunities
          </button>
          <div className="flex items-start gap-3 flex-wrap">
            <div className="flex-1 min-w-0">
              <h1 className="text-lg font-bold leading-snug" style={{ color: C.charcoal }}>
                {opp.opportunity_name || opp.name || "Opportunity"}
              </h1>
              <div className="flex items-center gap-2 mt-1 flex-wrap">
                <span
                  className="text-[11px] font-mono px-1.5 py-0.5 rounded"
                  style={{ background: C.sand, color: C.slate, border: `1px solid ${C.divider}` }}
                >
                  {opp.opportunity_number || opp.id?.slice(0, 8)}
                </span>
                {opp.account_name && (
                  <span className="flex items-center gap-1 text-xs" style={{ color: C.slate }}>
                    <Building2 size={11} /> {opp.account_name}
                  </span>
                )}
                {opp.contact_name && (
                  <span className="flex items-center gap-1 text-xs" style={{ color: C.slate }}>
                    <User size={11} /> {opp.contact_name}
                  </span>
                )}
                {opp.event_date && (
                  <span className="flex items-center gap-1 text-xs" style={{ color: C.slate }}>
                    <Calendar size={11} /> {opp.event_date}
                  </span>
                )}
              </div>
            </div>
            <span
              className="text-[11px] px-2.5 py-1 rounded-full font-medium shrink-0"
              style={{ background: stageColor.bg, color: stageColor.text, border: `1px solid ${stageColor.border}` }}
            >
              {STAGE_LABELS[opp.stage] || opp.stage}
            </span>
          </div>

          {/* Opportunity stage progress bar */}
          <div className="mt-2 -mx-2 border-t pt-1" style={{ borderColor: C.divider }}>
            <EventStageBar
              stages={OPP_STAGES}
              current={opp.stage}
              terminal={OPP_TERMINAL}
              accentHex={C.gold}
            />
          </div>

          {/* Inline validation hint when value missing */}
          {nextStage && PROPOSAL_STAGES.has(nextStage) && !opp.estimated_value && (
            <p
              data-testid="opp-value-required-hint"
              className="text-[10px] mt-1 px-1"
              style={{ color: "#B45309" }}
            >
              Estimated value required before advancing to {nextStageLabel}.
            </p>
          )}

          {/* Tabs */}
          <div className="flex gap-1 mt-2 overflow-x-auto">
            {TABS.map(t => {
              const Icon = t.icon;
              const active = tab === t.id;
              return (
                <button
                  key={t.id}
                  data-testid={`opp-tab-${t.id}`}
                  onClick={() => setTab(t.id)}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-full whitespace-nowrap transition-all"
                  style={{
                    background: active ? C.gold : "transparent",
                    color:      active ? "#fff" : C.slate,
                    border:     `1px solid ${active ? C.gold : C.divider}`,
                  }}
                >
                  <Icon size={11} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className={`max-w-6xl mx-auto ${primaryAction ? "pb-20" : ""}`}>
        {/* CRM-UX-03: Business Owner Intervention Banner */}
        {oppId && (
          <div className="px-4 sm:px-6 pt-3">
            <InterventionBanner recordType="opportunity" recordId={oppId} />
          </div>
        )}
        {tab === "overview"     && <OverviewTab opp={opp} />}
        {tab === "design"       && <MoodboardDesigner opportunityId={oppId} opportunityName={opp.opportunity_name || opp.name} />}
        {tab === "scope"        && <ScopeTab oppId={oppId} />}
        {tab === "requirements" && <RequirementsTab oppId={oppId} />}
        {tab === "event_brief"  && (
          <div className="p-6">
            <EventRequirementsPanel opportunityId={oppId} />
          </div>
        )}
        {tab === "commercial"   && <CommercialTab oppId={oppId} />}
        {tab === "booking"      && <BookingTab oppId={oppId} opp={opp} />}
        {tab === "eventcore"    && <EventCoreTab oppId={oppId} />}
      </div>

      {/* Workflow Action Bar */}
      <WorkflowActionBar
        visible={!!primaryAction}
        primary={primaryAction}
        hint={advanceHint}
      />
    </div>
  );
}
